/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'

// Third Party
import PropTypes from 'prop-types'

// OOTB Components
import {Box, HStack} from '@salesforce/retail-react-app/app/components/shared/ui'

// Custom Components
import ListPrice from './list-price'
import PricePerItem from './price-per-item'
import CurrentPrice from './current-price'

// custom hooks
import {useConversionRate} from '../../custom-contexts'

// utils
import {getConvertedCurrencyCode, getConvertedPrice} from '../../utils/price-utils'

/**
 * @param priceData - price info extracted from a product
 * // If a product is a set,
 *      on PLP, Show From X where X is the lowest of its children
 *      on PDP, Show From X where X is the lowest of its children and
 *          the set children will have it own price as From X (cross) Y
 * // if a product is a master
 *      on PLP and PDP, show From X (cross) Y , the X and Y are
 *          current and list price of variant that has the lowest price (including promotionalPrice)
 * // if a standard/bundle
 *      show exact price on PLP and PDP as X (cross) Y
 * @param currency - currency
 * @param quantity - quantity to take into the account for price display
 * @param currentPriceProps - extra props to be passing to CurrentPrice component
 * @param listPriceProps - extra props to be passing to ListPrice component
 * @param isCurrentPriceLast - boolean flag to determine the order of price display; if true, the current price is displayed after the list price
 * @param labelForA11y - label to be used for a11y
 * @param promoCallout - promoCallout component
 * @param {Object} props.conversionsRate - Conversion rate for orders on account pages (they coud be created with another ship-to flow).
 * @param {string} props.shipToCountryCode - The country code to which the item is shipped (it's for orders on account pages since they coud be created with another ship-to flow).
 */
const DisplayPrice = ({
    labelForA11y,
    priceData,
    currency,
    quantity = 1,
    currentPriceProps = {},
    listPriceProps = {},
    isCurrentPriceLast = true,
    isRenderPricePerUnit = true,
    isExchangeInvoicePrice = false,
    promoCallout,
    conversionsRate,
    shipToCountryCode,
    isBold = true,
    isLarge = true,
    isToHide = false,
    ...props
}) => {
    const contextConversionsRate = useConversionRate()
    const priceConversionsRate = conversionsRate || contextConversionsRate
    const {listPrice, currentPrice, beforePromoPrice, isASet, isMaster, isOnSale, isRange} =
        priceData
    const isOriginalPrice = isOnSale && beforePromoPrice

    /**
     * Renders the current price component with specified properties.
     *
     * @param {boolean} isRange - Determines if the price should be displayed as a range.
     * @param {boolean} isToHide - A flag indicating whether the price should be hidden.
     * @returns {JSX.Element} A JSX element representing the current price.
     *
     * The function utilizes the `CurrentPrice` component to display the price information.
     * It passes several props to the component:
     * - `labelForA11y`: A label for accessibility purposes.
     * - `price`: The converted price value, obtained by calling `getConvertedPrice` with the current price, basket, and a boolean flag.
     * - `as`: Specifies the HTML element type, in this case, a paragraph (`<p>`).
     * - `currency`: The currency code, retrieved using `getConvertedCurrencyCode` with the basket and currency.
     * - `isRange`: A boolean indicating if the price is a range.
     * - `variant`: The variant style for the price display, set to "bodyLarge1".
     * - `...currentPriceProps`: Additional properties spread into the component.
     */
    const renderCurrentPrice = (isRange, isToHide) => {
        return !isToHide ? (
            <CurrentPrice
                labelForA11y={labelForA11y}
                price={getConvertedPrice(currentPrice, priceConversionsRate, false)}
                as="p"
                currency={getConvertedCurrencyCode(priceConversionsRate, currency)}
                isRange={isRange}
                conversionsRate={conversionsRate}
                shipToCountryCode={shipToCountryCode}
                variant={
                    isLarge
                        ? isBold
                            ? 'bodyLarge1'
                            : 'bodyLarge2'
                        : isBold
                        ? 'bodyBase1'
                        : 'bodyBase2'
                }
                {...currentPriceProps}
            />
        ) : (
            <span>&nbsp;</span>
        )
    }

    /**
     * Renders the price per unit component conditionally based on the provided criteria.
     *
     * This function returns a JSX fragment that contains the `PricePerItem` component
     * if the following conditions are met:
     * - The `quantity` is greater than 1.
     * - The item is not a set (`isASet` is false).
     * - The `priceData` object contains a valid `pricePerUnit` value.
     *
     * The `PricePerItem` component is rendered with the specified `currency` and
     * `basePrice` derived from `priceData.pricePerUnit`.
     *
     * @returns {JSX.Element} A fragment containing the `PricePerItem` component if conditions are met, otherwise an empty fragment.
     */
    const renderPricePerUnit = () => (
        <>
            {quantity > 1 && !isASet && priceData?.pricePerUnit && (
                <PricePerItem
                    currency={getConvertedCurrencyCode(priceConversionsRate, currency)}
                    basePrice={getConvertedPrice(
                        priceData.pricePerUnit,
                        priceConversionsRate,
                        false
                    )}
                    conversionsRate={conversionsRate}
                    shipToCountryCode={shipToCountryCode}
                />
            )}
        </>
    )

    /**
     * Renders the ListPrice component:
     * case 1: render original price if before promo price
     * case 2: render "from list price" if a list price (highest tiered price for tiered pricing) is available (it's not used on VBQ project)
     *
     * @param {boolean} isRange - A flag indicating whether the price is a range.
     * @returns {JSX.Element|null} The ListPrice component with the appropriate props if a list price exists, otherwise null.
     *
     * The ListPrice component is rendered with the following props:
     * - `labelForA11y`: Accessibility label for the price.
     * - `currency`: The currency code derived from the basket and currency settings.
     * - `price`: The converted price value based on the list price and basket details.
     * - `isRange`: Indicates if the price should be displayed as a range.
     * - `variant`: The variant style for the price display, set to "bodyLarge1".
     * - Additional props spread from `listPriceProps`.
     *
     * This function is used to conditionally render the ListPrice component based on the presence of a list price.
     */
    const renderOriginalPrice = (originalPrice, isRange) =>
        originalPrice && (
            <ListPrice
                labelForA11y={labelForA11y}
                currency={getConvertedCurrencyCode(priceConversionsRate, currency)}
                price={getConvertedPrice(originalPrice, priceConversionsRate, false)}
                isRange={isRange}
                conversionsRate={conversionsRate}
                shipToCountryCode={shipToCountryCode}
                variant={
                    isLarge
                        ? isBold
                            ? 'bodyLarge1'
                            : 'bodyLarge2'
                        : isBold
                        ? 'bodyBase1'
                        : 'bodyBase2'
                }
                {...listPriceProps}
            />
        )

    /**
     * Renders a set of price components based on whether the price is a range and
     * the current pricing conditions such as sales and promo callout.
     *
     * @param {boolean} isRange - A boolean indicating if the price is a range.
     * @param {boolean} isToHide - A flag indicating whether the price should be hidden.
     * @returns {JSX.Element} A horizontal stack of price-related components,
     *                        including current price, list price, promotion,
     *                        and price per unit, arranged based on the
     *                        current pricing conditions.
     *
     * The function conditionally renders the price components in a specific order
     * depending on whether the current price is the last price to be displayed
     * and if the item is on sale. If the item is on sale, it includes the list
     * price. It is rendered promo callout if it's present. The order of rendering changes based on
     * the `isCurrentPriceLast` condition.
     */
    const renderPriceSet = (isRange, isToHide) => (
        <HStack spacing={6} wrap={'wrap'} alignItems={'baseline'}>
            {!isToHide ? (
                <>
                    {isCurrentPriceLast ? (
                        <>
                            {isOriginalPrice && renderOriginalPrice(beforePromoPrice, isRange)}

                            {/* render promoCallout after sale price */}
                            {!isExchangeInvoicePrice && isOnSale && promoCallout}

                            {renderCurrentPrice(isRange, isToHide)}

                            {/* render promoCallout at the end if no sale price */}
                            {!isExchangeInvoicePrice && !isOnSale && promoCallout}
                        </>
                    ) : (
                        <>
                            {renderCurrentPrice(isRange, isToHide)}
                            {promoCallout}
                            {isOriginalPrice && renderOriginalPrice(beforePromoPrice, isRange)}
                        </>
                    )}

                    {isRenderPricePerUnit && renderPricePerUnit()}
                </>
            ) : (
                <span>&nbsp;</span>
            )}
        </HStack>
    )

    if (isASet) {
        return renderCurrentPrice(true, isToHide)
    }

    if (isMaster) {
        return renderPriceSet(isRange, isToHide)
    }

    return <Box {...props}>{renderPriceSet(false, isToHide)}</Box>
}

DisplayPrice.propTypes = {
    priceData: PropTypes.shape({
        currentPrice: PropTypes.number.isRequired,
        beforePromoPrice: PropTypes.number,
        isOnSale: PropTypes.bool.isRequired,
        adjustments: PropTypes.array,
        listPrice: PropTypes.number,
        pricePerUnit: PropTypes.number,
        isASet: PropTypes.bool,
        isMaster: PropTypes.bool,
        isRange: PropTypes.bool,
        maxPrice: PropTypes.number,
        tieredPrice: PropTypes.number
    }),
    currency: PropTypes.string.isRequired,
    labelForA11y: PropTypes.string,
    currentPriceProps: PropTypes.object,
    listPriceProps: PropTypes.object,
    isCurrentPriceLast: PropTypes.bool,
    isRenderPricePerUnit: PropTypes.bool,
    isExchangeInvoicePrice: PropTypes.bool,
    quantity: PropTypes.number,
    promoCallout: PropTypes.object,
    conversionsRate: PropTypes.object,
    shipToCountryCode: PropTypes.string,
    isBold: PropTypes.bool,
    isLarge: PropTypes.bool,
    isToHide: PropTypes.bool
}

export default DisplayPrice
